<template>
  <main class="service-page">
    <div class="container">
      <div class="small-content">
        <h1 class="section-title">
          Privacy Policy – jeDAO Foundation (Mr. Canceller and jeDAO protocol)
        </h1>
        <span class="content-text">
          <strong>Effective Date:</strong> 05.01.2025
        </span>
        <h2 class="section-title small">
          1. Introduction
        </h2>
        <div class="content-text">
          <p>
            jeDAO Foundation is committed to maintaining your privacy and the security of your data. Our platform is a non-custodial, decentralized finance (DeFi) service that interacts with public blockchain networks and smart contracts.
          </p>
          <br />
          <p>
          This Privacy Policy explains what data we collect, how we use it, and your rights in relation to that data.
          </p>
        </div>
        <h2 class="section-title small">
          2. Data We Collect
        </h2>
        <div class="content-text">
          <p>
            We minimize data collection to align with decentralized and privacy-first principles. We may collect the following information:
          </p>
          <br />
          <h3 class="local-title">
            a. Public Blockchain Data
          </h3>
          <br />
          <ul class="service-page-list">
            <li>
              Wallet addresses
            </li>
            <li>
              Smart contract interactions
            </li>
            <li>
              Transaction history (public by nature)
            </li>
          </ul>
          <h3 class="local-title">
            b. Technical Information
          </h3>
          <br />
          <ul class="service-page-list">
            <li>
              Device type, operating system, and browser
            </li>
            <li>
              Connection data (e.g., Web3 provider used, timestamp of interactions)
            </li>
            <li>
              Referrer information (if applicable)
            </li>
          </ul>
          <h3 class="local-title">
            c. Optional Analytics (if enabled)
          </h3>
          <br />
          <ul class="service-page-list">
            <li>
              Aggregated, anonymized behavior data for platform improvement
            </li>
            <li>
              No personal identifiers are stored or used
            </li>
          </ul>
          <p>
            ! We do not collect names, email addresses, passwords, or other personally identifiable information (PII).
          </p>
        </div>
        <h2 class="section-title small">
          3. How We Use Your Information
        </h2>
        <div class="content-text">
          <p>
            We use the data we collect solely to:
          </p>
          <br />
          <ul class="service-page-list">
            <li>
              Deliver and optimize the Service
            </li>
            <li>
              Maintain security and detect abuse
            </li>
            <li>
              Analyze usage trends (aggregated and anonymized)
            </li>
            <li>
              Ensure compliance with applicable legal obligations (if any)
            </li>
          </ul>
          <p>
            We
              <span class="font-bold">
                do not
              </span>
            use your data for advertising or resale purposes.
          </p>
        </div>
        <h2 class="section-title small">
          4. Data Sharing
        </h2>
        <div class="content-text">
          <p>
            We do not sell, rent, or trade your information. We may share limited technical data only in the following circumstances:
          </p>
          <br />
          <ul class="service-page-list">
            <li>
              With service providers for performance analytics (anonymized)
            </li>
            <li>
              As required by applicable law, regulation, or court order
            </li>
            <li>
              To protect the rights or security of users and our infrastructure
            </li>
          </ul>
        </div>
        <h2 class="section-title small">
          5. Cookies and Local Storage
        </h2>
        <p class="content-text">
          Our front-end application may use cookies or local browser storage to remember interface preferences or sessions. You can clear these via your browser settings.
        </p>
        <h2 class="section-title small">
          6. Your Rights and Choices
        </h2>
        <div class="content-text">
          <p>
            Because we do not collect PII:
          </p>
          <br />
          <ul class="service-page-list">
            <li>
              We cannot access, edit, or delete data on your behalf.
            </li>
            <li>
              Data stored on public blockchains is immutable and beyond our control.
            </li>
          </ul>
          <p>
            You are responsible for managing your wallet and its associated data.
          </p>
          <h2 class="section-title small">
            7. Security
          </h2>
          <p class="content-text">
            We follow industry best practices to protect the front-end interface and related infrastructure. However, blockchain technology is inherently open and decentralized. You are responsible for safeguarding your private keys, wallet access, and device security.
          </p>
          <h2 class="section-title small">
            8. Updates
          </h2>
          <p class="content-text">
            This Privacy Policy may be updated from time to time. The latest version will always be available on our site or interface, and the "Last Updated" date will be revised accordingly.
          </p>
        </div>
      </div>
    </div>
  </main>
</template>

<script setup>
const config = useRuntimeConfig()

useHead({
  link: [
    {
      rel: 'canonical',
      href: `${config.public.appBaseUrl}/privacy-policy`
    },
  ]
})

useSeoMeta({
  title: getContent('seo.privacy_policy_title'),
  description: getContent('seo.privacy_policy_description'),
  ogTitle: getContent('seo.privacy_policy_og_title'),
  ogDescription: getContent('seo.privacy_policy_meta_description'),
  ogUrl: `${config.public.appBaseUrl}/privacy-policy`,
})
</script>